import React, { useState, useEffect } from "react";
import { Send, ArrowRight, Check, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscription, SubscriptionTier } from "@/hooks/useSubscription";
import { SEOHead } from "@/components/SEOHead";
import { analytics } from "@/lib/analytics";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

/* ────────────────────────────────────────────────────
   Pricing Page (Lavender Dream)
   ──────────────────────────────────────────────────── */

const GRADIENT_BG = `
  radial-gradient(ellipse 90% 70% at 5% -5%, rgba(175, 158, 252, 0.6) 0%, transparent 55%),
  radial-gradient(ellipse 70% 60% at 95% 8%, rgba(218, 148, 238, 0.45) 0%, transparent 52%),
  radial-gradient(ellipse 55% 45% at 50% 105%, rgba(155, 195, 245, 0.35) 0%, transparent 55%),
  radial-gradient(ellipse 45% 35% at 85% 55%, rgba(245, 158, 182, 0.28) 0%, transparent 48%),
  #ede9f5
`;

const SUPABASE_URL = (import.meta.env.VITE_SUPABASE_URL as string)?.replace(/\/$/, "");
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

const STARTER_PRICE_ID = import.meta.env.VITE_STRIPE_PRICE_ID_STARTER || "";
const PRO_PRICE_ID = import.meta.env.VITE_STRIPE_PRICE_ID_PRO || "";

// Tier ordering for upgrade/downgrade logic: free < starter < pro
const TIER_ORDER: Record<SubscriptionTier, number> = { free: 0, starter: 1, pro: 2 };

type CardState = "current" | "upgrade" | "downgrade" | "default";

const CARD_STYLE = {
  background: "rgba(255, 255, 255, 0.72)",
  backdropFilter: "blur(12px)",
  border: "1px solid rgba(255, 255, 255, 0.85)",
};

const CARD_STYLE_HIGHLIGHT = {
  ...CARD_STYLE,
  border: "2px solid rgba(124, 106, 240, 0.5)",
  boxShadow: "0 8px 32px rgba(124, 106, 240, 0.15)",
};

interface TierCardProps {
  title: string;
  price: string;
  period: string;
  features: string[];
  buttonText: string;
  onButtonClick: () => void;
  isPrimary?: boolean;
  isPopular?: boolean;
  loading?: boolean;
  isCurrentPlan?: boolean;
  cardState?: CardState;
}

const CARD_STYLE_CURRENT = {
  ...CARD_STYLE,
  border: "2px solid #10b981",
  boxShadow: "0 4px 20px rgba(16, 185, 129, 0.15)",
};

function TierCard({
  title,
  price,
  period,
  features,
  buttonText,
  onButtonClick,
  isPrimary = false,
  isPopular = false,
  loading = false,
  isCurrentPlan = false,
  cardState = "default",
}: TierCardProps) {
  // Determine card style: current plan > popular > default
  const getCardStyle = () => {
    if (isCurrentPlan) return CARD_STYLE_CURRENT;
    if (isPopular) return CARD_STYLE_HIGHLIGHT;
    return CARD_STYLE;
  };

  return (
    <div
      className="relative rounded-[20px] p-6 flex flex-col"
      style={getCardStyle()}
    >
      {isCurrentPlan && (
        <span
          className="absolute -top-3 left-4 px-3 py-1 text-[12px] font-medium text-white rounded-full"
          style={{ background: "linear-gradient(135deg, #10b981, #059669)" }}
        >
          Current Plan
        </span>
      )}
      {isPopular && !isCurrentPlan && (
        <span
          className="absolute -top-3 right-4 px-3 py-1 text-[12px] font-medium text-white rounded-full"
          style={{ background: "linear-gradient(135deg, #a898f8, #cc82e8)" }}
        >
          Most popular
        </span>
      )}

      <div className="mb-6">
        <h3
          className="text-[18px] font-semibold text-[#18151f] mb-2"
          style={{ fontFamily: "'DM Sans', sans-serif" }}
        >
          {title}
        </h3>
        <div className="flex items-baseline gap-1">
          <span
            className="text-[36px] text-[#18151f]"
            style={{ fontFamily: "'Instrument Serif', serif" }}
          >
            {price}
          </span>
          <span
            className="text-[14px] text-[#6b5f8a]"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            {period}
          </span>
        </div>
      </div>

      <ul className="space-y-3 mb-6 flex-1">
        {features.map((feature) => (
          <li key={feature} className="flex items-start gap-2">
            <Check className="h-4 w-4 text-[#7c6af0] mt-0.5 flex-shrink-0" />
            <span
              className="text-[14px] text-[#18151f]"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              {feature}
            </span>
          </li>
        ))}
      </ul>

      <button
        onClick={onButtonClick}
        disabled={loading || isCurrentPlan}
        className="w-full flex items-center justify-center gap-2 px-6 py-3 text-[14px] font-medium rounded-full transition-all disabled:opacity-50"
        style={{
          background: isCurrentPlan
            ? "rgba(16, 185, 129, 0.1)"
            : isPrimary
              ? "linear-gradient(135deg, #a898f8, #cc82e8)"
              : "rgba(255, 255, 255, 0.8)",
          color: isCurrentPlan ? "#059669" : isPrimary ? "#ffffff" : "#18151f",
          border: isCurrentPlan
            ? "1px solid rgba(16, 185, 129, 0.3)"
            : isPrimary
              ? "none"
              : "1px solid rgba(124, 106, 240, 0.3)",
          fontFamily: "'DM Sans', sans-serif",
          cursor: isCurrentPlan ? "default" : "pointer",
        }}
      >
        {loading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Redirecting...
          </>
        ) : (
          buttonText
        )}
      </button>
    </div>
  );
}

const Pricing = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { tier: currentTier, loading: subscriptionLoading } = useSubscription();
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackSending, setFeedbackSending] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState<"starter" | "pro" | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    analytics.visitedPricingPage();
  }, []);

  // Determine card state based on user's current tier
  const getCardState = (cardTier: SubscriptionTier): CardState => {
    // While loading or logged out, show default state
    if (!user || subscriptionLoading) return "default";

    if (cardTier === currentTier) return "current";
    if (TIER_ORDER[cardTier] > TIER_ORDER[currentTier]) return "upgrade";
    return "downgrade";
  };

  // Get appropriate button text based on card state
  const getButtonText = (cardTier: SubscriptionTier, defaultText: string): string => {
    const state = getCardState(cardTier);
    if (state === "current") return "Manage Subscription";
    if (state === "upgrade") return `Upgrade to ${cardTier.charAt(0).toUpperCase() + cardTier.slice(1)}`;
    if (state === "downgrade") return `Switch to ${cardTier.charAt(0).toUpperCase() + cardTier.slice(1)}`;
    return defaultText;
  };

  // Handle button click based on card state
  const getButtonAction = (cardTier: SubscriptionTier, defaultAction: () => void): () => void => {
    const state = getCardState(cardTier);
    if (state === "current") return () => navigate("/settings");
    return defaultAction;
  };

  const handleSubscribe = async (tier: "starter" | "pro") => {
    if (!user) {
      navigate(`/auth?next=${encodeURIComponent("/pricing")}`);
      return;
    }

    const priceId = tier === "starter" ? STARTER_PRICE_ID : PRO_PRICE_ID;
    if (!priceId) {
      toast.error("Stripe is not configured. Contact support@usedayone.net");
      return;
    }

    setCheckoutLoading(tier);
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout-session", {
        body: {
          priceId,
          successUrl: `${window.location.origin}/account?checkout=success`,
          cancelUrl: `${window.location.origin}/pricing?checkout=cancelled`,
        },
      });

      if (error) {
        throw new Error(error.message || "Failed to create checkout session");
      }

      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (err) {
      console.error("[Pricing] Checkout error:", err);
      toast.error(err instanceof Error ? err.message : "Failed to start checkout");
      setCheckoutLoading(null);
    }
  };

  const handleFreeClick = async () => {
    // If not logged in or on free tier, navigate to create/auth
    if (!user || currentTier === "free") {
      if (user) {
        navigate("/create");
      } else {
        navigate("/auth?next=/create");
      }
      return;
    }

    // Paid user clicking "Switch to Free" -> open Stripe portal to cancel
    setPortalLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-portal-session");
      if (error) {
        throw new Error(error.message || "Failed to open billing portal");
      }
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No portal URL returned");
      }
    } catch (err) {
      console.error("[Pricing] Portal error:", err);
      toast.error("Could not open billing portal. Try again.");
      setPortalLoading(false);
    }
  };

  const handleSubmitFeedback = async () => {
    if (!feedbackText.trim()) return;
    setFeedbackSending(true);
    try {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        apikey: SUPABASE_ANON_KEY,
      };
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) {
        headers.Authorization = `Bearer ${session.access_token}`;
      }

      const res = await fetch(`${SUPABASE_URL}/functions/v1/submit-feedback`, {
        method: "POST",
        headers,
        body: JSON.stringify({ message: feedbackText.trim() }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Failed to send feedback");
      }

      toast.success("Thanks for the feedback!");
      setFeedbackText("");
    } catch (err) {
      console.error("[Pricing] feedback error:", err);
      toast.error("Failed to send feedback. Please try again.");
    } finally {
      setFeedbackSending(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: GRADIENT_BG }}>
      <SEOHead
        title="Pricing — DayOne"
        description="Pick a plan and start building. From browsing ideas to deploying apps. Cancel anytime."
      />

      <div className="max-w-5xl mx-auto px-6 py-16 md:py-24">
        {/* Hero */}
        <div className="text-center mb-12">
          <h1
            className="text-[40px] md:text-[48px] text-[#18151f] mb-4"
            style={{ fontFamily: "'Instrument Serif', serif" }}
          >
            Pick a plan. Start building.
          </h1>
          <p
            className="text-[18px] text-[#6b5f8a] max-w-xl mx-auto"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            From browsing ideas to deploying apps. Cancel anytime.
          </p>
        </div>

        {/* Tier Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          <TierCard
            title="Free"
            price="$0"
            period="forever"
            features={[
              "Browse all 157+ ideas",
              "1 free build to try it out",
              "Community support",
            ]}
            buttonText={getButtonText("free", "Get started")}
            onButtonClick={getButtonAction("free", handleFreeClick)}
            isPrimary={false}
            isCurrentPlan={getCardState("free") === "current"}
            cardState={getCardState("free")}
            loading={portalLoading && currentTier !== "free"}
          />
          <TierCard
            title="Starter"
            price="$29"
            period="/month"
            features={[
              "Full reports on 10 starter ideas",
              "Up to 3 apps",
              "30 AI edits per month",
              "1 custom domain",
              "Email support",
            ]}
            buttonText={getButtonText("starter", "Subscribe to Starter")}
            onButtonClick={getButtonAction("starter", () => handleSubscribe("starter"))}
            isPrimary={getCardState("starter") !== "current"}
            isPopular={true}
            loading={checkoutLoading === "starter"}
            isCurrentPlan={getCardState("starter") === "current"}
            cardState={getCardState("starter")}
          />
          <TierCard
            title="Pro"
            price="$69"
            period="/month"
            features={[
              "Full reports on all 157+ ideas",
              "Unlimited apps & deployments",
              "150 AI edits per month",
              "Unlimited custom domains",
              "Priority build queue",
              "Early access to agents",
              "Priority email support",
            ]}
            buttonText={getButtonText("pro", "Subscribe to Pro")}
            onButtonClick={getButtonAction("pro", () => handleSubscribe("pro"))}
            isPrimary={getCardState("pro") !== "current"}
            loading={checkoutLoading === "pro"}
            isCurrentPlan={getCardState("pro") === "current"}
            cardState={getCardState("pro")}
          />
        </div>

        {/* Feedback Form */}
        <div className="max-w-2xl mx-auto mb-16">
          <div
            className="text-left rounded-[20px] p-8"
            style={CARD_STYLE}
          >
            <h2
              className="text-[20px] text-[#18151f] mb-2"
              style={{ fontFamily: "'Instrument Serif', serif" }}
            >
              Help shape what's next
            </h2>
            <p
              className="text-[14px] text-[#6b5f8a] mb-5"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              What would you pay for? What's missing?
            </p>
            <textarea
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              placeholder="I'd pay for..."
              rows={4}
              className="w-full rounded-xl px-4 py-3 text-[14px] text-[#18151f] placeholder:text-[#6b5f8a]/50 focus:outline-none resize-none mb-4"
              style={{
                fontFamily: "'DM Sans', sans-serif",
                background: "rgba(255, 255, 255, 0.8)",
                border: "1px solid rgba(124, 106, 240, 0.3)",
              }}
            />
            <button
              onClick={handleSubmitFeedback}
              disabled={!feedbackText.trim() || feedbackSending}
              className="flex items-center gap-2 px-6 py-2.5 text-[14px] font-medium text-white rounded-full transition-all disabled:opacity-50"
              style={{
                background: "linear-gradient(135deg, #a898f8, #cc82e8)",
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              <Send className="h-4 w-4" />
              {feedbackSending ? "Sending..." : "Send Feedback"}
            </button>
          </div>
        </div>

        {/* FAQ */}
        <div className="max-w-2xl mx-auto">
          <h2
            className="text-center text-[24px] text-[#18151f] mb-8"
            style={{ fontFamily: "'Instrument Serif', serif" }}
          >
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            <div className="rounded-[16px] p-6" style={CARD_STYLE}>
              <h3
                className="text-[16px] font-semibold text-[#18151f] mb-2"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                Can I switch plans anytime?
              </h3>
              <p
                className="text-[14px] text-[#6b5f8a]"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                Yes. Upgrade or downgrade whenever you want. Changes take effect immediately, and we prorate the difference.
              </p>
            </div>
            <div className="rounded-[16px] p-6" style={CARD_STYLE}>
              <h3
                className="text-[16px] font-semibold text-[#18151f] mb-2"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                What happens if I cancel?
              </h3>
              <p
                className="text-[14px] text-[#6b5f8a]"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                You'll keep access until the end of your billing period, then drop to the Free tier. Your projects remain accessible forever.
              </p>
            </div>
            <div className="rounded-[16px] p-6" style={CARD_STYLE}>
              <h3
                className="text-[16px] font-semibold text-[#18151f] mb-2"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                Do you offer refunds?
              </h3>
              <p
                className="text-[14px] text-[#6b5f8a]"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                No refunds, but you can cancel anytime. Once you cancel, you'll keep access until the end of your billing period and won't be charged again. If you hit a real issue, email support@usedayone.net and we'll work something out.
              </p>
            </div>
            <div className="rounded-[16px] p-6" style={CARD_STYLE}>
              <h3
                className="text-[16px] font-semibold text-[#18151f] mb-2"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                This is beta — should I wait?
              </h3>
              <p
                className="text-[14px] text-[#6b5f8a]"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                Honest answer: if you want rock-solid, wait a few months. If you want to build something today and help shape DayOne, jump in. Bugs will happen; we fix fast.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
